<template>
  <base-modal ref="modal" @hiding="reset()">
    <base-card>
      <h3 class="tw-text-xl tw-font-thin">New Activity Type</h3>
      <base-form ref="form" v-model="isFormValid" class="tw-mt-5">
        <base-text-field label="Label" type="text" placeholder="Enter a label for the activity type" v-model="todoItemType.label" @keyup.enter="save" required />
        <base-text-field
          label="Group Name"
          type="text"
          placeholder="Enter a group name for the activity type"
          v-model="todoItemType.typeGroup"
          @keyup.enter="save"
          class="tw-pb-5"
        />
        <base-textarea label="Description" placeholder="Enter a description" v-model="todoItemType.description" @keyup.enter="save" required />
        <div class="tw-mt-3">
          <base-advanced-select
            label="Medications"
            placeholder="Select one or more Medications"
            :options="groupedAndSortedMedications"
            :value="selectedMedications"
            @input="onMedicationsChange"
            label-key="label"
            value-key="value"
            multiple
            class="tw-flex-grow"
          />
        </div>
        <div class="tw-mt-4">
          <div v-if="isLoadingRxItems">
            <span>Loading Rx Items...</span>
          </div>
          <base-advanced-select
            v-else
            label="Rx Items"
            placeholder="Select one or more Rx Items"
            :options="groupedAndSortedRxItems"
            :value="selectedRxItems"
            @input="onRxItemsChange"
            label-key="label"
            value-key="value"
            multiple
            class="tw-flex-grow tw-mt-4"
          />
        </div>
        <div class="tw-mt-3">
          <base-checkbox label="Allow assign" v-model="todoItemType.isAssignable" />
        </div>
      </base-form>
      <div class="sm:tw-flex sm:tw-flex-row-reverse">
        <base-button @click="save" :loading="isLoading" class="tw-w-full sm:tw-w-auto sm:tw-ml-3" :disabled="!isFormValid">
          Create
        </base-button>
        <base-button @click="hide" type="flat" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
          Cancel
        </base-button>
      </div>
      <inline-error class="tw-mt-3" id="add-business-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
    </base-card>
  </base-modal>
</template>

<script>
import InlineError from "@/components/InlineError";
import orderBy from "lodash/orderBy";

export default {
  name: "NewTodoItemType",
  components: {
    InlineError,
  },
  props: {
    businessId: String,
    allRxItems: {
      type: Array,
      default: () => [],
    },
    allMedications: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      isFormValid: true,
      isLoadingRxItems: false,
      todoItemType: {
        label: null,
        typeGroup: null,
        description: null,
        target: "member",
        isAssignable: false,
      },
      selectedRxItems: [],
      selectedMedications: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    groupedAndSortedRxItems() {
      if (!Array.isArray(this.allRxItems) || this.allRxItems.length === 0) {
        return [];
      }

      const suppliesItems = this.allRxItems.filter((item) => item.productGroup === "Supplies");

      const mappedItems = suppliesItems.map((item) => ({
        ...item,
        label: typeof item.name === "string" && item.name.trim() !== "" ? item.name : item.label && item.label.trim() !== "" ? item.label : "Unnamed Rx Item",
        value: item.businessItemCategoryId,
        group: item.productGroup || null,
      }));

      const options = [];
      const groups = [...new Set(mappedItems.map((item) => item.group))].filter(Boolean);

      groups.forEach((group) => {
        options.push({ label: group, value: null, isHeader: true });
        const itemsInGroup = mappedItems.filter((i) => i.group === group).map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(itemsInGroup, ["label"]));
      });

      const noGroupItems = mappedItems.filter((item) => !item.group);
      if (noGroupItems.length > 0) {
        options.push({ label: "Other Rx Items", value: null, isHeader: true });
        const mappedNoGroup = noGroupItems.map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(mappedNoGroup, ["label"]));
      }
      return options;
    },
    groupedAndSortedMedications() {
      if (!Array.isArray(this.allMedications) || this.allMedications.length === 0) {
        return [];
      }
      const mappedItems = this.allMedications.map((item) => ({
        ...item,
        label: typeof item.name === "string" && item.name.trim() !== "" ? item.name : item.label && item.label.trim() !== "" ? item.label : "Unnamed Medications",
        value: item.businessItemCategoryId,
        group: item.productGroup || null,
      }));

      const options = [];
      const groups = [...new Set(mappedItems.map((item) => item.group))].filter(Boolean);

      // Sort groups alphabetically (ascending)
      const sortedGroups = orderBy(groups, [(group) => group.toLowerCase()], ["asc"]);

      sortedGroups.forEach((group) => {
        options.push({ label: group, value: null, isHeader: true });
        const itemsInGroup = mappedItems.filter((i) => i.group === group).map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(itemsInGroup, [(item) => (isNaN(item.label) ? item.label.toLowerCase() : parseFloat(item.label))], ["asc"]));
      });

      const noGroupItems = mappedItems.filter((item) => !item.group);
      if (noGroupItems.length > 0) {
        options.push({ label: "Other Medications", value: null, isHeader: true });
        const mappedNoGroup = noGroupItems.map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(mappedNoGroup, [(item) => (isNaN(item.label) ? item.label.toLowerCase() : parseFloat(item.label))], ["asc"]));
      }

      return options;
    },
  },
  methods: {
    reset() {
      this.todoItemType = {
        label: null,
        typeGroup: null,
        description: null,
        target: "member",
        isAssignable: false,
      };
      this.selectedRxItems = [];
      this.selectedMedications = [];
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
      if (this.$refs.form && typeof this.$refs.form.reset === "function") this.$refs.form.reset();
    },
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
      this.reset();
    },
    save() {
      if (!this.$refs.form.validate()) return;
      this.isLoading = true;
      const businessItemCategoryIds = this.selectedRxItems
        .map((item) => {
          if (typeof item === "string" || typeof item === "number") {
            return item;
          } else if (item && item.value) {
            return item.value;
          }
          return null;
        })
        .filter((id) => id !== null);

      const medicationIds = (this.selectedMedications || [])
        .map((item) => (typeof item === "string" || typeof item === "number" ? item : item && item.value ? item.value : null))
        .filter((id) => id !== null && id !== undefined);
      this.$axios
        .post(`/businesses/${this.businessId}/todo/types`, {
          label: this.todoItemType.label,
          typeGroup: this.todoItemType.typeGroup,
          description: this.todoItemType.description,
          target: this.todoItemType.target,
          isAssignable: this.todoItemType.isAssignable,
          businessItemCategoryIds,
          medicationIds,
        })
        .then(() => {
          this.$emit("typesUpdated", {});
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Activity Type Saved",
            message: `Your activity type was saved.`,
          });
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = `Error creating activity type`;
          this.errorMessage = "Sorry, we had trouble creating this activity type. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onRxItemsChange(selected) {
      this.selectedRxItems = Array.isArray(selected) ? selected : [];
    },
    onMedicationsChange(selected) {
      this.selectedMedications = Array.isArray(selected) ? selected : [];
    },
  },
};
</script>
